/**
 * 모의투자 매매 규칙이 바뀐 날. 비교 차트에 세로 점선으로 찍는다. (2026-09-12)
 *
 * 🔴 왜 DB 가 아니라 코드인가
 *   changelog 와 같은 이유다. 규칙은 코드(설정)가 바뀌어야 바뀌고, 그 변경은
 *   커밋에 남는다. 따로 테이블을 두면 코드는 바뀌었는데 표시는 안 바뀐 채
 *   조용히 어긋난다. 규칙을 고치는 커밋에 이 배열도 함께 고친다 - 한 커밋에서
 *   둘이 같이 움직여야 "어느 날부터 다른 규칙으로 굴렀나" 를 몇 달 뒤에도
 *   믿을 수 있다. 회차 초기화(관리자 화면)와 달리 사용자가 화면에서
 *   바꾸는 값이 아니므로 DB 에 있을 이유가 없다.
 *
 * 🔴 곡선 비교에서 이 선이 갖는 뜻
 *   선 앞뒤는 다른 규칙으로 굴린 구간이다. 한 회차 안에서 규칙이 바뀌었으면
 *   그 회차의 성적은 두 규칙이 섞인 값이라 회차끼리 견줄 때 그 사실을 셈해야 한다.
 */

export interface RuleChange {
  /** yyyy-MM-dd. 규칙이 실제로 적용되기 시작한 날(배포일) */
  date: string;
  /** 차트 위에 찍히는 짧은 라벨. 폰에서 겹치지 않게 열 글자 안쪽으로 */
  label: string;
  /** 차트 아래 한 줄 설명. 무엇이 어떻게 바뀌었고 언제부터 반영되는지 */
  detail: string;
}

export const RULE_CHANGES: RuleChange[] = [
  {
    date: '2026-09-10',
    label: '저점매수(리스크) 익절 +3%→+4%',
    detail: '2회차 도중 적용. 회차 비교는 3회차부터',
  },
  {
    date: '2026-09-12',
    label: '저점매수 감시 대상 50→100종목',
    // 🔴 그날의 사실을 적되 지금 시각과 갈린 것을 밝힌다 (2026-09-20)
    //   재선정 배치가 그때는 16:05 였고 2026-09-19 에 21:00 으로 옮겨졌다(I-52 A안).
    //   "16:05" 만 남겨 두면 이 줄을 지금 읽는 사람이 현재 시각으로 오해한다.
    detail: '다음 재선정부터 반영. 당시 마감 후 배치가 16:05 였다 (지금은 21:00)',
  },
  {
    // I-52 A안. 배포는 9/19(토)지만 첫 거래일이 3회차 시작일이다
    date: '2026-09-21',
    label: '3회차 저점매수 규칙',
    // 🔴 같은 날 두 줄은 점선이 겹쳐 하나로 보인다(테스트가 막는다) - 3회차 시작에 함께 바뀐 것을 한 줄에 적는다
    //   I-52 매매 시간 + I-62(112번 35절) 챌린저 상위 30 · 손절선 아래 매수 금지 (2026-09-19)
    //   + 2회차 회고 점검(112번 36 · 37절) 저점매수 손절 1.5 ATR · 리스크 익절 +5% (2026-09-20 사용자 결정)
    //   + 추천 계좌 파는 쪽만 20:00 까지 · 만료 19:50 (2026-09-20 사용자 결정, PaperIntradayJob)
    //   ✏️ 2026-09-29 I-57 — 추천 계좌 장전 NXT 매수는 켜 뒀지만 저장 누락으로 3회차에 한 번도 안 돌았다(매수 9건 전부 09:00:30).
    //     "삼" 이라고 두면 이 선이 없던 변화를 가리킨다. 사용자 결정으로 4회차부터(trading.yml from-round: 4)
    detail:
      '저점매수 매매 시간 09:00~15:30 → 08:00~20:00 · 챌린저는 왕복성 상위 30종목만 새로 삼 · ' +
      '저점매수 세 계좌는 이미 손절선 아래 가격이면 사지 않음 · 저점매수 손절 2 → 1.5 ATR · ' +
      '저점매수(리스크) 익절 +4% → +5% · 추천 계좌 NXT 장전 매수는 오류로 3회차에는 돌지 않음(4회차부터) · ' +
      '추천 계좌 목표 · 손절 감시 15:30 → 20:00, 만료 정리 15:10 → 19:50. 3회차 시작부터',
  },
  {
    // U-31 회차 = 성적표 마감일 (129번 3.3b · 2026-09-24 사용자 결정 · 10/9 병합으로 배포)
    //   매매 규칙은 그대로고 "회차 끝에 팔지 않는다" 만 바뀐다. 그래도 선을 찍는 이유 —
    //   3회차까지는 회차 끝 청산 손익이 성적에 들어갔고(저점매수 두 계좌 −125만 원), 4회차부터는
    //   넘어온 보유의 평가손익이 들어간다. 회차끼리 견줄 때 이 차이를 셈해야 한다 (2026-09-29 사용자 결정)
    //   🔴 날짜는 3회차 마감일이다 — 그날 종가로 성적표를 적고 팔지 않는 첫날. 곡선에 그날이 없으면 선은 안 그려진다
    date: '2026-10-16',
    label: '회차 끝 보유 넘김',
    detail: '회차가 끝나도 팔지 않고 보유를 다음 회차로 넘김 · 마감일 평가액으로 성적표만 적음. 3회차 마감부터',
  },
  {
    // 🆕 4회차 첫 국내 거래일 (2026-10-16 화면 점검 · 4회차를 10/16 밤이든 10/17 이든 열면 첫 국내 거래일은 10/19)
    //   함께 바뀐 것을 한 줄에(같은 날 두 줄은 테스트가 막는다): 추천 3 → 1(141번 10.6) · 스윙 둘(141번 12.6) ·
    //   추천 장전 NXT 매수(from-round 4) · 같은 종목 겹친 매수 막음(same-stock-guard) · 챌린저 보유 상한 없앰(until-round 3)
    date: '2026-10-19',
    label: '4회차 규칙',
    detail:
      '분할익절 · 하이리스크 새 매수 쉼(보유만 정리) · 스윙(6개월) · 스윙(3개월) 시작 · 추천(안전) 장전 NXT 매수 · ' +
      '들고 있는 종목은 새로 추천돼도 다시 안 삼 · 저점매수(챌린저) 보유 상한 없앰. 4회차 시작부터',
  },
];

/**
 * 차트에 실제로 찍을 수 있는 규칙 변경만 남긴다.
 *
 * 🔴 곡선 날짜(curveDates)에 없는 날은 그릴 수 없다
 *   x 축이 범주형(날짜 문자열)이라 축에 없는 값을 ReferenceLine 에 주면
 *   Recharts 가 조용히 아무것도 안 그린다. 주말·공휴일에 배포했거나
 *   아직 그날 스냅샷이 안 찍혔으면 선이 없는 것이 정상이고, 아래 안내
 *   문구도 그 날짜를 빼야 "선이 있다고 했는데 없다" 가 안 된다.
 *
 * 날짜순(오름차순)으로 돌려준다. 입력 배열은 바꾸지 않는다.
 */
export function ruleChangesOnCurve(changes: RuleChange[], curveDates: string[]): RuleChange[] {
  const onCurve = new Set(curveDates);
  return changes
    .filter((change) => onCurve.has(change.date))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * 점선 위 라벨을 선의 어느 쪽으로 뻗게 할지.
 *
 * 🔴 규칙 변경은 대개 "최근" 이라 선이 차트 오른쪽 끝 근처에 선다
 *   라벨을 선 가운데 정렬(middle)로 두면 절반이 차트 밖으로 나가 잘린다
 *   (svg 는 넘친 글자를 숨긴다). 오른쪽 절반에 선 선은 라벨을 왼쪽으로(end),
 *   왼쪽 절반이면 오른쪽으로(start) 뻗게 해 늘 차트 안에 남긴다.
 *   시간이 지나 선이 왼쪽으로 옮겨 가면 방향도 따라 바뀐다.
 *
 * @param dateIndex 곡선 날짜 배열에서 그 날의 위치. 없으면(-1) 왼쪽으로 친다
 * @param count 곡선 날짜 수
 */
export function ruleLabelAnchor(dateIndex: number, count: number): 'start' | 'end' {
  if (count <= 1 || dateIndex < 0) {
    return 'start';
  }
  return dateIndex >= count / 2 ? 'end' : 'start';
}
